import { Tooltip } from '@mui/material';
import { GridCell, GridCellProps, GridRenderCellParams, GridRenderEditCellParams } from '@mui/x-data-grid-premium';
import { RiAlertLine, RiLockLine } from '@remixicon/react';
import { useMemo } from 'react';

import type { GridColValue } from '../DataGrid';
import { useEditModeTabStop } from '../keyboard/useEditModeTabStop';

import PopoverEditCell from './PopoverEditCell';
import { useEditableGridIssue } from './useEditableGridIssue';
import { useEditableGridRows } from './useEditableGridRows';

export const EDITABLE_GRID_CELL_STYLE = {
  '& .ErroredCell': { backgroundColor: '#FDF7F7 !important' },
  '& .WarningCell': { backgroundColor: '#FFFBE6 !important' },
};

/**
 * The view half of a grid cell: content, plus at most ONE annotation.
 *
 *   validation issue ──▶ alert icon  + issue message   (outranks a lock)
 *   lock reason      ──▶ lock icon   + lock reason
 *   neither          ──▶ bare content, no tooltip (MUI renders none for '')
 */
export const LockableCellContent = ({
  issue,
  lockReason,
  content,
}: {
  issue: { message: string; warning?: true } | null;
  lockReason: string | null;
  content: React.ReactNode;
}) => {
  const tooltip = issue?.message ?? lockReason ?? '';

  return (
    <Tooltip title={tooltip} arrow>
      <div className="flex items-center w-full">
        <div className="flex gap-2 items-center w-full h-full">
          {content}
          {issue ? (
            <RiAlertLine
              data-testid="cell-issue-icon"
              size={18}
              className={`ml-auto ${issue.warning ? 'text-orange-text' : 'text-red-text'}`}
            />
          ) : lockReason ? (
            <RiLockLine
              data-testid="cell-lock-icon"
              size={16}
              aria-label={lockReason}
              className="ml-auto text-neutral-text"
            />
          ) : null}
        </div>
      </div>
    </Tooltip>
  );
};

const OverloadedViewCell = ({
  rowId,
  column,
  renderParams,
}: {
  rowId: number;
  column: GridCellProps['column'];
  renderParams: GridRenderCellParams;
}) => {
  const issue = useEditableGridIssue(rowId, column.field);
  const row = useEditableGridRows((store) => store.getLocalRow(rowId));

  return (
    <LockableCellContent
      issue={issue}
      lockReason={(column as GridColValue).lockReason?.(row) ?? null}
      content={
        // Formatted Value is passed from internal GridCell:
        // https://github.com/mui/mui-x/blob/master/packages/x-data-grid/src/components/cell/GridCell.tsx#L421C11-L421C26
        column.renderCell?.(renderParams) ?? renderParams.formattedValue
      }
    />
  );
};

export type EditableGridCellProps = GridCellProps & { instanceGridId: string; editorZIndex?: number };

export default function EditableGridCell({ instanceGridId, editorZIndex, ...props }: EditableGridCellProps) {
  const column = useMemo(() => {
    const renderCell = (params: GridRenderCellParams) => {
      return <OverloadedViewCell column={props.column} rowId={params.id as number} renderParams={params} />;
    };

    // MUI hydrates `renderEditCell` onto every typed column (gridStringColDef.js:25), so the
    // pop-out is UNIVERSAL — there is no per-column opt-out and `rendered` is never absent.
    const renderEditCell = (params: GridRenderEditCellParams) => (
      <PopoverEditCell instanceGridId={instanceGridId} editorZIndex={editorZIndex} {...params}>
        {props.column.renderEditCell?.(params)}
      </PopoverEditCell>
    );

    return { ...props.column, renderCell, renderEditCell };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- props.rowId intentionally excluded to prevent column remount on row change (breaks tab navigation focus)
  }, [props.column, instanceGridId, editorZIndex]);

  const issue = useEditableGridIssue(props.rowId as number, column.field);
  const row = useEditableGridRows((store) => store.getLocalRow(props.rowId as number));
  const isEditable = useMemo(() => (column as GridColValue).isCellEditable?.(row) !== false, [column, row]);
  const className = useMemo(() => {
    const classNames = [];

    if (issue) classNames.push(issue.warning ? 'WarningCell' : 'ErroredCell');

    if (!isEditable) classNames.push('Mui-disabled');

    return classNames.join(' ');
  }, [issue, isEditable]);

  // Spread, NEVER a plain prop: `GridCell` applies `...other` after its own tabIndex, so an
  // explicit `undefined` would erase the view-mode tab stop.
  const editModeTabStop = useEditModeTabStop(props.rowId, column.field);

  // The disabled styling and the accessible readonly state are the same fact.
  return (
    <GridCell
      {...props}
      {...(editModeTabStop === undefined ? {} : { tabIndex: editModeTabStop })}
      aria-readonly={!isEditable}
      className={className}
      column={column}
    />
  );
}
